// src/composables/useUser.js
import { ref, computed, onMounted } from 'vue';
import userService from '@/services/userService';

/**
 * 用户信息管理的 Composable
 * 提供响应式的用户信息状态和相关操作方法
 * 使用单例模式，确保全局只有一个实例
 */

// 全局单例状态（在模块作用域中定义，所有组件共享）
const userProfile = ref(null);
const isLoading = ref(false);
const error = ref(null);
let isInitialized = false; // 标记是否已经初始化过
let initPromise = null; // 保存初始化 Promise，避免并发初始化

export function useUser() {

  // 计算属性
  const isLoggedIn = computed(() => {
    return !!userProfile.value && !!localStorage.getItem('authToken');
  });

  const username = computed(() => {
    return userProfile.value?.username || '';
  });

  const nickname = computed(() => {
    return userProfile.value?.nickname || userProfile.value?.username || '';
  });

  const level = computed(() => {
    return userProfile.value?.level || 1;
  });

  const levelText = computed(() => {
    return userService.getLevelText(level.value);
  });

  const exp = computed(() => {
    return userProfile.value?.exp || 0;
  });

  const studyStreak = computed(() => {
    return userProfile.value?.studyStreak || 0;
  });

  const totalStudyTime = computed(() => {
    return userProfile.value?.totalStudyTime || 0;
  });

  const formattedStudyTime = computed(() => {
    return userService.formatStudyTime(totalStudyTime.value);
  });

  const avatar = computed(() => {
    return userProfile.value?.avatar || null;
  });

  const email = computed(() => {
    return userProfile.value?.email || '';
  });

  const isActive = computed(() => {
    return userService.isUserActive();
  });

  const isStatusNormal = computed(() => {
    return userService.isUserStatusNormal();
  });

  // 方法
  const fetchUserProfile = async () => {
    // 如果正在加载中，避免重复请求
    if (isLoading.value) {
      console.log('用户信息正在加载中，跳过重复请求');
      return userProfile.value;
    }

    try {
      isLoading.value = true;
      error.value = null;

      const profile = await userService.getUserProfile();
      console.log("获取用户信息成功：", profile)
      if (profile) {
        userProfile.value = profile;
        return profile;
      } else {
        error.value = '获取用户信息失败';
        return null;
      }
    } catch (err) {
      error.value = err.message || '获取用户信息时发生错误';
      console.error('获取用户信息失败:', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const refreshUserProfile = () => {
    return fetchUserProfile();
  };

  const updateUserProfile = (updates) => {
    if (userProfile.value) {
      userProfile.value = { ...userProfile.value, ...updates };
      userService.updateUserProfile(updates);
    }
  };

  const clearUserData = () => {
    userProfile.value = null;
    error.value = null;
    isInitialized = false; // 重置初始化标志
    initPromise = null; // 清除初始化 Promise
    userService.clearUserData();
  };

  // 初始化用户信息
  const initializeUser = async () => {
    // 如果已经初始化过或已有用户数据，直接返回
    if (isInitialized || userProfile.value) {
      return;
    }
    
    // 如果正在初始化中，返回现有的 Promise
    if (initPromise) {
      return initPromise;
    }
    
    // 如果有token，则获取用户信息
    if (localStorage.getItem('authToken')) {
      isInitialized = true; // 标记为已初始化
      initPromise = fetchUserProfile().finally(() => {
        initPromise = null; // 完成后清除 Promise
      });
      return initPromise;
    }
  };

  // 组件挂载时初始化（只在第一次调用时执行）
  onMounted(() => {
    initializeUser();
  });

  return {
    // 状态
    userProfile,
    isLoading,
    error,
    
    // 计算属性
    isLoggedIn,
    username,
    nickname,
    level,
    levelText,
    exp,
    studyStreak,
    totalStudyTime,
    formattedStudyTime,
    avatar,
    email,
    isActive,
    isStatusNormal,
    
    // 方法
    fetchUserProfile,
    refreshUserProfile,
    updateUserProfile,
    clearUserData,
    initializeUser
  };
}
